import { Box, Stack } from "@mui/material";
import { useMemo } from "react";

import DetailedViewPageHeader, {
  DetailedViewPageHeaderProps,
} from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import { EntityChipList } from "@/common/components/EntityInfo/EntityChipList";
import Image from "@/common/components/Images/Image";
import { EntityHelper } from "@/common/helpers/entity";
import { MakeLogoHelper } from "@/common/helpers/makeLogo";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { VehicleDtoAll, VehicleDtoAny } from "@/common/mapping/mappers/vehicle";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { EntityViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  BaseEntityDto,
  GeneralAttachedTagsDto,
  MimeBaseType,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import AppIcon from "../../../../Icons/AppIcon";
import GeneralAttachedTagsDisplay from "../../../General/GeneralTag/GeneralAttachedTagsDisplay";
import VehicleLink from "../../VehicleLink";

interface Props<TEntity extends BaseEntityDto> extends Partial<DetailedViewPageHeaderProps> {
  operationType: VehicleOperationType;
  operationEntity: TEntity;
  /** Set to fetch vehicle. */
  vehicleId: string | null | undefined;
  vehicle: Nil<VehicleDtoAny>;
  // secondaryActions?: ReactNode;
  // primaryActions?: ReactNode;
  onTagsUpdated: (newTags: Nil<GeneralAttachedTagsDto>) => void | Promise<void>;
}

export function VehicleOperationViewPageHeader<TEntity extends BaseEntityDto>({
  operationType,
  operationEntity,
  vehicleId,
  vehicle,
  // secondaryActions,
  // primaryActions,
  onTagsUpdated,
  ...otherProps
}: Props<TEntity>) {
  const entityMeta = useMemo(() => EntityHelper.getEntityMeta(operationEntity), [operationEntity]);

  const vehicleRequest = useApiRequest(
    apiClient.vehiclesApi.apiV1VehiclesVehicleIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      vehicleId: vehicleId!,
    },
    {
      deps: [vehicleId, vehicle],
      skip: !vehicleId || !!vehicle,
    },
  );
  const vehicleFetched = vehicleRequest?.data;
  const vehicleAll = (vehicleFetched || vehicle) as Nil<VehicleDtoAll>;

  const vehicleImageUrl = useMemo(() => {
    const imageAttachment = vehicleAll?.attachments?.find(
      (x) =>
        x.file?.mimeBaseType === MimeBaseType.Image || x.file?.contentType?.indexOf("image") !== -1,
    );
    return imageAttachment?.file?.url;
  }, [vehicleAll]);
  const vehicleMakeUrl = useMemo(() => {
    return MakeLogoHelper.getMakeLogoByIdentifier(vehicleAll?.spec?.make?.identifier);
  }, [vehicleAll]);

  return (
    <DetailedViewPageHeader
      {...otherProps}
      image={
        <>
          {vehicleImageUrl && (
            <Image
              src={vehicleImageUrl}
              sx={{
                height: "92px",
                width: "auto",
                borderRadius: "6px",
                objectFit: "cover",
              }}
            />
          )}
          {!vehicleImageUrl && vehicleMakeUrl && (
            <Image
              src={vehicleMakeUrl}
              sx={{
                height: "92px",
                width: "92px",
                borderRadius: "6px",
                objectFit: "contain",
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: (th) => th.palette.divider,
              }}
            />
          )}
          {!vehicleImageUrl && !vehicleMakeUrl && (
            <Box
              sx={{
                width: "92px",
                height: "92px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "background.gray",
              }}
            >
              <AppIcon
                of='vehicle'
                sx={{ width: "46px", height: "46px", fill: (th) => th.palette.divider }}
              />
            </Box>
          )}
        </>
      }
      title={
        otherProps.title || (
          <>
            {StringHelper.join(
              [
                enumService.getEnumValueName(ApiEnumName.VehicleOperationType, operationType),
                entityMeta?.localNumber || entityMeta?.globalNumber,
              ],
              ", ",
              { skipEmpty: true },
            )}{" "}
            <EntityChipList
              entity={operationEntity}
              entityViewVariant={EntityViewVariant.View}
              variant='normal'
            />
          </>
        )
      }
      subtitle={
        otherProps.subtitle || (
          <Stack spacing={0.5}>
            <Box>
              {/* {StringHelper.joinIntoString(
              [
                vehicleAll?.plateNo?.plateNo,
                vehicleAll?.identificationNumber,
                vehicleAll?.spec?.make?.name,
                vehicleAll?.spec?.model?.name,
              ],
              " • ",
              { skipEmpty: true },
            )} */}
              <VehicleLink
                entityId={vehicleAll?.id}
                entity={vehicleAll}
                inlineProps={{ withIcon: true }}
              />
            </Box>

            <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
              <GeneralAttachedTagsDisplay
                tags={entityMeta?.tags}
                defaultIsFolded={false}
                edit={{
                  entityType: operationType,
                  entityId: operationEntity?.id,
                  onSaved: (newValue) => {
                    onTagsUpdated(newValue);
                  },
                }}
              />
            </AuthorizedElement>
          </Stack>
        )
      }
      // primaryActions={
      //   <Stack direction='row' mr={1.5} spacing={1} sx={{ justifyContent: "flex-end" }}>
      //     <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
      //       {primaryActions}
      //     </Stack>
      //   </Stack>
      // }
      // secondaryActions={
      //   <Stack direction='row' spacing={1} sx={{ justifyContent: "flex-end" }}>
      //     <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
      //       {secondaryActions}
      //     </Stack>
      //   </Stack>
      // }
    />
  );
}
